<script>
/**
 * @component BaseSubNav
 * @description Barra de navegación secundaria (page-toolbar) con tabs y acciones.
 *              Estilo card compacto. El título se oculta cuando hay navegación visible,
 *              ya que la topbar ya muestra el título de la página.
 * @example <BaseSubNav navigationType="appointment" :showNavigation="true" />
 */
import { mapGetters } from "vuex";
import { enableChat, isMassiveEnabled } from "@/services/config";

export default {
  name: "BaseSubNav",

  props: {
    navigationType: {
      type: String,
      default: null,
      validator: function (value) {
        return (
          value === null ||
          [
            "dashboard-alerts",
            "treatment",
            "appointment",
            "settings",
            "validation",
            "administration",
            "prescription",
          ].includes(value)
        );
      },
    },
    showNavigation: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isMassiveEnabled,
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isAssistant", "isDoctor", "isPatient"]),

    enableChat() {
      return enableChat;
    },

    isTreatmentSubNavActive() {
      return (
        this.$route.name === "treatment" ||
        this.$route.name === "treatment-tasks"
      );
    },

    isConfigSubNavActive() {
      return (
        this.$route.name &&
        this.$route.name !== "treatment-tasks" &&
        (this.$route.name.includes("treatment-") ||
          this.$route.name === "create-vital" ||
          this.$route.name === "update-vital")
      );
    },
  },

  methods: {
    isRoute(name) {
      return this.$route.name === name;
    },
  },
};
</script>

<template lang="pug">
section.page-toolbar-container
  .page-toolbar(:class="{ 'page-toolbar--with-nav': showNavigation }")
    //- Título: solo visible cuando NO hay navegación
    h1.page-toolbar__title(v-if="!showNavigation")
      slot(name="title")

    //- Tabs de navegación
    .page-toolbar__tabs(v-if="showNavigation")
      slot(name="navigation")
        //- Navegación de Seguimientos
        template(v-if="navigationType === 'dashboard-alerts'")
          router-link.page-toolbar__tab(
            :to="{ name: 'dashboard-alerts' }"
            :class="{ 'page-toolbar__tab--active': isRoute('dashboard-alerts') }"
          ) Todos
          router-link.page-toolbar__tab(
            v-if="isAdmin || isAssistant"
            :to="{ name: 'assistant-evaluation' }"
            :class="{ 'page-toolbar__tab--active': isRoute('assistant-evaluation') }"
          ) Derivaciones Asesores
          router-link.page-toolbar__tab(
            v-if="isDoctor && enableChat"
            :to="{ name: 'dashboard-conversations' }"
            :class="{ 'page-toolbar__tab--active': isRoute('dashboard-conversations') }"
          ) Conversaciones
          router-link.page-toolbar__tab(
            v-if="(isAdmin || isAssistant) && enableChat"
            :to="{ name: 'dashboard-conversations-unreplied' }"
            :class="{ 'page-toolbar__tab--active': isRoute('dashboard-conversations-unreplied') }"
          ) Auditoría Chat

        //- Navegación de Administración
        template(v-if="navigationType === 'administration'")
          router-link.page-toolbar__tab(
            :to="{ name: 'administration' }"
            :class="{ 'page-toolbar__tab--active': isRoute('administration') }"
          ) Administración
          router-link.page-toolbar__tab(
            :to="{ name: 'administration-history' }"
            :class="{ 'page-toolbar__tab--active': isRoute('administration-history') }"
          ) Historial

        //- Navegación de Prescripciones
        template(v-if="navigationType === 'prescription'")
          router-link.page-toolbar__tab(
            :to="{ name: 'prescription-queue' }"
            :class="{ 'page-toolbar__tab--active': isRoute('prescription-queue') }"
          ) Administración
          router-link.page-toolbar__tab(
            :to="{ name: 'prescription-history' }"
            :class="{ 'page-toolbar__tab--active': isRoute('prescription-history') }"
          ) Historial

        //- Navegación de Tratamientos
        template(v-if="navigationType === 'treatment'")
          router-link.page-toolbar__tab(
            :to="{ name: 'treatment' }"
            :class="{ 'page-toolbar__tab--active': isTreatmentSubNavActive }"
          ) Protocolos
          el-dropdown(trigger="click")
            a.page-toolbar__tab.page-toolbar__tab--dropdown(
              :class="{ 'page-toolbar__tab--active': isConfigSubNavActive }"
            )
              | Configuración
              i.el-icon-arrow-down.el-icon--right
            el-dropdown-menu(slot="dropdown")
              el-dropdown-item(:disabled="isRoute('treatment-medication')" :class="{ active: isRoute('treatment-medication') }")
                router-link(:to="{ name: 'treatment-medication' }") Medicaciones
              el-dropdown-item(:disabled="isRoute('treatment-appointment')" :class="{ active: isRoute('treatment-appointment') }")
                router-link(:to="{ name: 'treatment-appointment' }") Recordatorios
              el-dropdown-item(:disabled="isRoute('treatment-recommendation')" :class="{ active: isRoute('treatment-recommendation') }")
                router-link(:to="{ name: 'treatment-recommendation' }") Recomendaciones
              el-dropdown-item(:disabled="isRoute('treatment-exercise')" :class="{ active: isRoute('treatment-exercise') }")
                router-link(:to="{ name: 'treatment-exercise' }") Ejercicios
              el-dropdown-item(:disabled="isRoute('treatment-vital')" :class="{ active: isRoute('treatment-vital') || isRoute('update-vital') }")
                router-link(:to="{ name: 'treatment-vital' }") Registros
              el-dropdown-item(:disabled="isRoute('treatment-report')" :class="{ active: isRoute('treatment-report') }")
                router-link(:to="{ name: 'treatment-report' }") Reportes
              el-dropdown-item(:disabled="isRoute('treatment-checkin')" :class="{ active: isRoute('treatment-checkin') }")
                router-link(:to="{ name: 'treatment-checkin' }") Check-in

        //- Navegación de Citas/Agenda
        template(v-if="navigationType === 'appointment'")
          router-link.page-toolbar__tab(
            :to="{ name: 'appointment-calendar' }"
            :class="{ 'page-toolbar__tab--active': isRoute('appointment-calendar') }"
          ) Calendario
          router-link.page-toolbar__tab(
            v-if="isAdmin || isDoctor"
            :to="{ name: 'appointment-agendas' }"
            :class="{ 'page-toolbar__tab--active': isRoute('appointment-agendas') }"
          ) {{ isDoctor ? "Mis agendas" : "Agendas" }}
          router-link.page-toolbar__tab(
            v-if="!isPatient"
            :to="{ name: 'appointment-relocation' }"
            :class="{ 'page-toolbar__tab--active': isRoute('appointment-relocation') }"
          ) Citas a reprogramar

        //- Navegación de Validación
        template(v-if="navigationType === 'validation'")
          router-link.page-toolbar__tab(
            :to="{ name: 'validation' }"
            :class="{ 'page-toolbar__tab--active': isRoute('validation') }"
          ) Validar
          router-link.page-toolbar__tab(
            :to="{ name: 'validation-history' }"
            :class="{ 'page-toolbar__tab--active': isRoute('validation-history') }"
          ) Historial

        //- Navegación de Configuración
        template(v-if="navigationType === 'settings'")
          router-link.page-toolbar__tab(
            :to="{ name: 'settings' }"
            :class="{ 'page-toolbar__tab--active': isRoute('settings') }"
          ) Administración
          router-link.page-toolbar__tab(
            v-if="isMassiveEnabled"
            :to="{ name: 'settings-massive' }"
            :class="{ 'page-toolbar__tab--active': isRoute('settings-massive') }"
          ) Carga Masiva

    .page-toolbar__actions
      slot(name="actions")
</template>

<style scoped>
.page-toolbar-container {
  width: 100%;
  margin-bottom: 12px;
}

.page-toolbar {
  width: 100%;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
}

.page-toolbar__title {
  font-size: 1.2rem;
  font-weight: bold;
  color: #040055;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-toolbar__tabs {
  display: flex;
  align-items: center;
  gap: 0;
}

.page-toolbar__tab {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;
}

.page-toolbar__tab:hover {
  color: #002d5e;
}

.page-toolbar__tab--active {
  color: #002d5e;
  font-weight: 600;
  border-bottom: 2px solid #002d5e;
}

.page-toolbar__tab--dropdown {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .page-toolbar__title {
    font-size: 1rem;
  }

  .page-toolbar__actions {
    flex-wrap: wrap;
    gap: 6px;
  }

  .page-toolbar__tab {
    padding: 6px 10px;
    font-size: 12px;
  }
}

::v-deep .el-switch__label {
  white-space: nowrap;
  font-size: 13px;
  padding: 0 4px;
}

::v-deep .el-dropdown-menu {
  .el-dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 8px 16px;
  }

  .el-dropdown-item.active {
    background: #f1f4f9;
    color: #040055;
  }

  .el-dropdown-item a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
    color: inherit;
    width: 100%;
  }
}
</style>
